import { memo, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { EntityListTimeSeparator } from '@aimsos/ui';

import { groupActivityByDate } from '../../utils/groupActivityByDate.js';
import ActivityCard from './ActivityCard.jsx';

/**
 * The feed, grouped under its date headings (Today, A month ago, …).
 *
 * The bucketing lives in `groupActivityByDate` and returns i18n KEYS; the
 * translation happens here, so neither the util nor the card holds copy. Each
 * heading is a `role="separator"` from the design system, and each group a
 * labelled region so a screen reader can jump between them. `highlightedId`
 * marks the event a History link named (CT-5724).
 */
const ActivityGroupList = memo(function ActivityGroupList({ items, highlightedId = null, onPreviewNote }) {
  const { t } = useTranslation('unified-profiles');

  // `now` is read once per items change: re-reading it per render would move a
  // card between headings while the user is looking at it.
  const groups = useMemo(() => groupActivityByDate(items, new Date()), [items]);

  return (
    <div data-testid="activity-groups" className="flex flex-col gap-3">
      {groups.map((group) => {
        const label = t(group.labelKey, { count: group.count });

        return (
          <section key={group.id} aria-label={label} className="flex flex-col gap-3">
            <EntityListTimeSeparator label={label} />
            {group.items.map((item) => (
              <ActivityCard
                key={item.id}
                item={item}
                highlighted={item.id === highlightedId}
                onPreview={onPreviewNote}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
});

export default ActivityGroupList;
